/**
 * Copy `text` to the clipboard with a graceful fallback for browsers /
 * contexts where the async Clipboard API is unavailable or throws.
 *
 * The async `navigator.clipboard` API is only exposed in secure contexts
 * (HTTPS or localhost) AND requires the document to be focused. In
 * non-secure contexts Chrome leaves `navigator.clipboard` undefined and any
 * `writeText` call throws — when callers fire-and-forget the Promise the
 * rejection is swallowed and any "Copied!" UI still flashes, leaving the
 * user thinking the copy worked when nothing was actually written. That was
 * the root cause of the VMS Settings "copy email" button silently failing
 * in Chrome. Same problem can hit the VMS Emails preview-panel copy
 * actions.
 *
 * Strategy:
 *   1. Try `navigator.clipboard.writeText` when defined. Returns true on
 *      success, falls through on rejection (permissions denied, focus
 *      issue, non-secure context where the call still exists but errors).
 *   2. Fall back to the legacy `document.execCommand('copy')` path via a
 *      temporary, off-screen `<textarea>`. Marked `readonly` +
 *      `aria-hidden` and positioned absolutely off the viewport so it
 *      doesn't visually flash. Works in every browser we care about,
 *      including HTTP contexts and old Edge.
 *
 * Returns true if either path succeeded, false if both failed. Callers
 * should gate their success UI on this boolean instead of assuming the
 * copy worked.
 */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  // Preferred path: async Clipboard API. The `?.` guard handles the case
  // where navigator.clipboard is undefined (HTTP contexts in Chrome).
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch {
      // Fall through to the textarea fallback. Don't surface the error —
      // the fallback below succeeds in practice for the failure modes we
      // see (focus, transient permission prompts, non-secure context).
    }
  }

  // Legacy fallback. Required for HTTP-served instances and any browser
  // where the async API rejects. The textarea is added, selected, copied,
  // then removed in the same tick so it never appears in the user's tab
  // order or visually on screen.
  if (typeof document === "undefined") return false
  const textarea = document.createElement("textarea")
  textarea.value = text
  textarea.setAttribute("readonly", "")
  textarea.setAttribute("aria-hidden", "true")
  textarea.style.position = "absolute"
  textarea.style.left = "-9999px"
  textarea.style.top = "0"
  document.body.appendChild(textarea)
  try {
    textarea.focus()
    textarea.select()
    textarea.setSelectionRange(0, text.length)
    const ok = document.execCommand("copy")
    return ok
  } catch {
    return false
  } finally {
    document.body.removeChild(textarea)
  }
}
